import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, MapPin, Users, Calendar, Waves, Trees, Mountain, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [availabilityNotice, setAvailabilityNotice] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);

  useEffect(() => {
    // Charger l'annonce de disponibilité depuis l'API
    const loadAvailabilityNotice = async () => {
      try {
        const response = await axios.get(`${API}/content/availability_notice`);
        if (response.data) {
          setAvailabilityNotice(response.data);
        }
      } catch (error) {
        console.log('Pas d\'annonce de disponibilité');
      }
    };
    
    loadAvailabilityNotice();
  }, []);

  const features = [
    {
      icon: <HomeIcon className="w-6 h-6" />,
      title: "4 Chambres",
      description: "3 chambres doubles et 1 chambre à 2 lits simples",
      image: "/images/villa/villa-bedroom-v2.jpg",
      details: "La villa dispose de 4 chambres confortables : une chambre double (160cm) avec salle de douche & WC, une chambre double (160cm), une chambre à 2 lits simples (90cm) et une chambre double (140cm) avec salle de douche & WC."
    },
    {
      icon: <Waves className="w-6 h-6" />,
      title: "Spa de Nage",
      description: "D'Avril à Novembre à 30°C",
      image: "/images/villa/villa-spa-v2.jpg",
      details: "Profitez d'Avril à Novembre d'un spa de nage chauffé à une température constante de 30 degrés. Un moment de détente privilégié dans un cadre exceptionnel."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Capacité",
      description: "Jusqu'à 8 personnes",
      image: "/images/villa/villa-living-v2.jpg",
      details: "La Villa Nomade peut accueillir confortablement jusqu'à 8 personnes avec 4 chambres, 3 salles de bain et de grands espaces de vie pour toute la famille."
    },
    {
      icon: <Trees className="w-6 h-6" />,
      title: "Jardin 700m²",
      description: "Espace extérieur ensoleillé",
      image: "/images/villa/villa-terrace-v2.jpg",
      details: "Un magnifique jardin de 700m² ensoleillé avec terrasses, salon de jardin et vue sur les ânes Tom et Nomade, vos voisins très gentils et calmes."
    },
    {
      icon: <Mountain className="w-6 h-6" />,
      title: "Golf de Trousse Chemise",
      description: "À 2 pas du green",
      image: "/images/seo/golf-v2.jpg",
      details: "Avis aux amateurs de golf, la villa Nomade est située à 2 pas du green du prestigieux golf de Trousse Chemise. Un parcours 9 trous exceptionnel entre océan et marais."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Plages Proches",
      description: "3 Cailloux, La Loge, La Patache",
      images: [
        {
          url: "https://customer-assets-gfyr7b9c.emergentagent.net/job_villa-nomade-re/artifacts/d8rwb9y5_IMG_6040.jpeg",
          caption: "Plage des 3 Cailloux"
        },
        {
          url: "https://customer-assets-gfyr7b9c.emergentagent.net/job_villa-nomade-re/artifacts/5g5zdi6c_IMG_6039.jpeg",
          caption: "Plage de la Loge"
        },
        {
          url: "https://customer-assets-gfyr7b9c.emergentagent.net/job_villa-nomade-re/artifacts/cio0l0bb_IMG_6041.jpeg",
          caption: "Plage de la Patache"
        }
      ],
      details: "À proximité immédiate des plus belles plages de l'île de Ré : la plage des 3 Cailloux sauvage et préservée, la plage de la Loge avec le club nautique pour les activités nautiques, et la magnifique plage de la Patache réputée pour ses couchers de soleil spectaculaires sur le Banc du Bûcheron."
    }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Villa Nomade Les Portes en Ré - Location Vacances Île de Ré avec Spa"
        description="Villa de luxe 4 chambres avec spa de nage aux Portes en Ré, île de Ré. Proche golf de Trousse Chemise et plages magnifiques. Location saisonnière pour 8 personnes."
        keywords="villa ile de ré, location portes en ré, villa nomade, golf trousse chemise, location vacances ile de ré, spa ile de ré, villa avec piscine ile de ré"
      />
      {/* Hero Section */}
      <section className="relative h-[75vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/villa/villa-exterior-v2.jpg"
            alt="Villa Nomade aux Portes-en-Ré"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        </div>
        <div className="relative h-full flex flex-col items-center justify-center text-white px-4">
          <div className="text-center max-w-4xl">
            <div className="mb-6">
              <div className="w-28 h-28 mx-auto rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center border-4 border-white/30 shadow-2xl">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 22V12H15V22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-serif mb-4 tracking-wide">Balade aux Portes en Ré</h1>
            <h2 className="text-3xl md:text-4xl font-light mb-6">Villa NOMADE</h2>
            <p className="text-lg md:text-xl mb-8 text-stone-100">Une villa d'exception entre golf, plages et marais salants</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/reservation">
                <Button size="lg" className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Réservation
                </Button>
              </Link>
              <Link to="/photos">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-amber-800 px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                  Album Photos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-amber-700 font-medium mb-2">Référencement d'hébergement N°17286 000168 PF</p>
          <h3 className="text-3xl md:text-4xl font-serif mb-6 text-stone-800">Bienvenue à la Villa Nomade</h3>
          
          {/* Annonce de disponibilité dynamique */}
          {availabilityNotice && availabilityNotice.content && (
            <div className="mb-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg">
              {availabilityNotice.title && (
                <h4 className="text-xl font-semibold text-amber-800 mb-2">
                  {availabilityNotice.title}
                </h4>
              )}
              <p className="text-lg text-amber-900 font-medium">
                {availabilityNotice.content}
              </p>
            </div>
          )}
          
          <p className="text-lg text-stone-600 leading-relaxed mb-4">
            La villa Nomade est située entre le golf de Trousse Chemise, la plage des 3 cailloux et celle de la Loge 
            avec l'un des sites nautiques du Club Nautique des Portes en Ré. Un endroit parfait pour conjuguer détente et sport.
          </p>
          <p className="text-lg text-stone-600 leading-relaxed">
            Cette maison conviviale et confortable est composée de 4 chambres, 3 salles de bains et 3 places de parking intérieures.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white cursor-pointer group"
                onClick={() => setSelectedFeature(feature)}
              >
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center text-amber-700 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h4 className="text-xl font-semibold mb-2 text-stone-800 group-hover:text-amber-700 transition-colors">
                    {feature.title}
                  </h4>
                  <p className="text-stone-600">{feature.description}</p>
                  <p className="text-xs text-amber-600 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    Cliquez pour voir plus
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Modal */}
      <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
        <DialogContent className="max-w-4xl p-0 bg-white border-none">
          {selectedFeature && (
            <div className="relative">
              <button
                onClick={() => setSelectedFeature(null)}
                className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white text-stone-800 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="grid md:grid-cols-2">
                {/* Image or Carousel */}
                <div className="relative h-[400px] md:h-auto bg-stone-100">
                  {selectedFeature.images ? (
                    // Carousel for multiple images
                    <Carousel className="w-full h-full">
                      <CarouselContent>
                        {selectedFeature.images.map((img, index) => (
                          <CarouselItem key={index}>
                            <div className="relative h-[400px] md:h-[500px]">
                              <img
                                src={img.url}
                                alt={img.caption}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                                <p className="text-white text-lg font-medium">{img.caption}</p>
                              </div>
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-4" />
                      <CarouselNext className="right-4" />
                    </Carousel>
                  ) : (
                    // Single image
                    <img
                      src={selectedFeature.image}
                      alt={selectedFeature.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                
                {/* Content */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="mb-4 flex items-center gap-3 text-amber-700">
                    <div className="p-3 bg-amber-50 rounded-full">
                      {selectedFeature.icon}
                    </div>
                    <h3 className="text-3xl font-serif text-stone-800">{selectedFeature.title}</h3>
                  </div>
                  <p className="text-lg text-stone-700 leading-relaxed mb-6">
                    {selectedFeature.details}
                  </p>
                  {selectedFeature.images && (
                    <p className="text-sm text-stone-500 italic mb-6">
                      Utilisez les flèches pour voir les 3 plages ◀ ▶
                    </p>
                  )}
                  <div className="flex gap-3">
                    <Link to="/photos" className="flex-1">
                      <Button 
                        className="w-full bg-amber-700 hover:bg-amber-800 text-white transition-all duration-300 hover:scale-105"
                        onClick={() => setSelectedFeature(null)}
                      >
                        Voir l'album photos
                      </Button>
                    </Link>
                    <Link to="/reservation" className="flex-1">
                      <Button 
                        variant="outline" 
                        className="w-full border-2 border-amber-700 text-amber-700 hover:bg-amber-50 transition-all duration-300 hover:scale-105"
                        onClick={() => setSelectedFeature(null)}
                      >
                        Réserver
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Spa Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <img
                src="/images/villa/villa-spa-v2.jpg"
                alt="Spa de nage"
                className="w-full h-[400px] object-cover rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h3 className="text-3xl font-serif mb-4 text-stone-800">Spa de Nage</h3>
              <p className="text-lg text-stone-600 leading-relaxed mb-4">
                Vous profiterez d'Avril à Novembre d'un spa de nage à une température de 30 degrés.
              </p>
              <p className="text-lg text-stone-600 leading-relaxed">
                Un moment de détente privilégié dans un cadre exceptionnel, entouré par la nature paisible de l'Île de Ré.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Villa Details */}
      <section className="py-16 px-4 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-serif mb-8 text-center text-stone-800">La Villa en Détail</h3>
          <div className="bg-white rounded-lg shadow-md p-8">
            <p className="text-lg text-stone-700 mb-6">
              La villa Nomade, d'une surface de <strong>150m²</strong> et d'un jardin de <strong>700m²</strong>, comprend :
            </p>
            <ul className="space-y-3 text-stone-700">
              <li className="flex items-start">
                <span className="text-amber-700 mr-2">•</span>
                <span>Une chambre double (160cm) avec salle de douche & WC</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-700 mr-2">•</span>
                <span>Une chambre double (160cm)</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-700 mr-2">•</span>
                <span>Une chambre à 2 lits simples (90cm)</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-700 mr-2">•</span>
                <span>Une chambre double (140cm) avec salle de douche & WC</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-700 mr-2">•</span>
                <span>Une salle de douche & WC supplémentaire</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-700 mr-2">•</span>
                <span>WC indépendants</span>
              </li>
            </ul>
            <p className="text-lg text-stone-700 mt-6">
              La maison possède une pièce de vie comprenant une cuisine ouverte sur la salle à manger, 
              un salon s'ouvrant sur deux terrasses et un jardin ensoleillé.
            </p>
            <p className="text-lg text-stone-700 mt-4 italic">
              Du jardin, vous pourrez même apercevoir les 2 voisins : Tom et Nomade, deux ânes très gentils et très calmes.
            </p>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-serif mb-8 text-center text-stone-800">Un Emplacement Idéal</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <img
                src="/images/seo/golf-v2.jpg"
                alt="Golf de Trousse Chemise"
                className="w-full h-[350px] object-cover rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-4">
              <p className="text-lg text-stone-700 leading-relaxed">
                Cette maison est idéale pour des vacances au calme, située dans un lieu protégé de l'Île de Ré. 
                Entre marais et les belles plages de Trousse Chemise.
              </p>
              <p className="text-lg text-stone-700 leading-relaxed">
                Avis aux amateurs de golf, la villa Nomade est à 2 pas du green.
              </p>
              <p className="text-lg text-stone-700 leading-relaxed">
                Vous entendrez probablement dire que la plage de la Patache fait partie des plus belles plages de l'Île de Ré, 
                c'est la réalité... Admirez le coucher du soleil sur le Banc du Bûcheron ou dégustez des huîtres 
                aux Huîtres de Trousse Chemise dans les marais, est toujours un régal pour les yeux...
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Moyenne Saison & Séminaires Promo */}
      <section className="py-16 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-serif mb-3 text-stone-800">L'Île de Ré Toute l'Année</h3>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto">
              Au-delà de l'été, découvrez la villa en moyenne saison et pour vos séminaires — au calme et à prix doux.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/moyenne-saison-ile-de-re" className="group block bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="h-48 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1752606302768-8953e76056dd?w=800)' }}></div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2 text-amber-700">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wide">Avril · Mai · Septembre · Octobre</span>
                </div>
                <h4 className="text-xl font-serif text-stone-800 mb-2 group-hover:text-amber-700 transition-colors">Vacances en Moyenne Saison</h4>
                <p className="text-stone-600 text-sm">Vélos offerts, golf ouvert, pêche, visites ornithologiques et tarifs très attractifs pour une île authentique et paisible.</p>
                <span className="inline-block mt-4 text-amber-700 font-medium group-hover:underline">Découvrir →</span>
              </div>
            </Link>
            <Link to="/seminaire-ile-de-re" className="group block bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
              <div className="h-48 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1633457896836-f8d6025c85d1?w=800)' }}></div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2 text-amber-700">
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-semibold uppercase tracking-wide">Séminaires & Team Building</span>
                </div>
                <h4 className="text-xl font-serif text-stone-800 mb-2 group-hover:text-amber-700 transition-colors">Séminaires de Direction</h4>
                <p className="text-stone-600 text-sm">Grande table de réunion, Wifi performant 150 Mbit/s et cadre inspirant pour réunir et fédérer vos équipes.</p>
                <span className="inline-block mt-4 text-amber-700 font-medium group-hover:underline">En savoir plus →</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-50 to-stone-100">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-4xl font-serif mb-6 text-stone-800">Prêt à Réserver?</h3>
          <p className="text-xl text-stone-700 mb-8">
            Contactez-nous dès maintenant pour vérifier les disponibilités et réserver votre séjour à la Villa Nomade.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/reservation">
              <Button size="lg" className="bg-amber-700 hover:bg-amber-800 text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
                Voir les Disponibilités
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white px-10 py-6 text-lg transition-all duration-300 hover:scale-105">
                Nous Contacter
              </Button>
            </Link>
          </div>
          <div className="mt-8">
            <Link to="/ile-de-re" className="text-amber-700 hover:text-amber-800 underline">
              En savoir plus sur l'Île de Ré et Les Portes en Ré →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;